import type { FC } from 'react';

import { ArrowClockwise, ArrowCounterClockwise, MusicNotes } from '@phosphor-icons/react/dist/ssr';

import { cn } from '@/utils';

import { formatAudioPlayerTime } from '../utils/formatAudioPlayerTime';
import { useAudioPlayer } from '../utils/useAudioPlayer';

import * as styles from './AudioPlayer.css';
import type { PlayerControlProps } from './AudioPlayer.type';
import PlayButton from './PlayButton';
import { PlaybackSpeedControl } from './PlaybackSpeedControl';
import useWaveformCanvas from './useWaveformCanvas';

interface DetailedPlayerProps extends PlayerControlProps {
  subtitle?: string;
}

export const DetailedPlayer: FC<DetailedPlayerProps> = ({
  subtitle,
  audioUrl,
  audioType = 'audio/mpeg',
  thumbnailUrl,
  title,
  duration: knownDuration,
  primaryColor,
  secondaryColor,
  backgroundColor,
  textColor,
  iconColor: iconColorProp,
  showThumbnail,
  showTitle,
  showRewind,
  showForward,
  rewindSeconds,
  forwardSeconds,
  playButtonSize,
  skipButtonSize,
  waveformHeight,
  waveform,
  onPlayAttempt,
  isPlaybackLocked,
  isPreview,
  audioElementId,
  playbackStorageKey,
}) => {
  const {
    audioRef,
    isPlaying,
    isReady,
    isBuffering,
    currentTime,
    duration,
    progress,
    srcAttached,
    playbackRate,
    setPlaybackRate,
    togglePlay,
    handleRewind,
    handleForward,
    seekTo,
  } = useAudioPlayer({ rewindSeconds, forwardSeconds, knownDuration, playbackStorageKey, enablePlaybackSpeed: true });

  const { canvasRef, isDecoding, handleCanvasClick, handleCanvasKeyDown } = useWaveformCanvas({
    audioUrl,
    title,
    waveform,
    progress,
    currentTime,
    duration,
    seekTo,
    primaryColor,
    secondaryColor,
    barWidth: 4,
    barGap: 3,
    resampleOnResize: true,
  });

  const iconColor = iconColorProp || textColor || primaryColor;

  return (
    <div className={styles.detailedContainer}>
      <div
        className={cn(
          styles.playerWrapper,
          styles.detailedWrapper,
          isPlaybackLocked && 'pointer-events-none cursor-not-allowed',
        )}
        style={{ backgroundColor }}
      >
        {showThumbnail &&
          (thumbnailUrl ? (
            <img src={thumbnailUrl} alt="" className={cn(styles.thumbnail, styles.detailedArtwork)} />
          ) : (
            <div className={styles.detailedArtwork} aria-hidden="true">
              <MusicNotes size={40} weight="regular" />
            </div>
          ))}

        <div className={cn(styles.playerContent, styles.detailedContent)}>
          <div className={cn(styles.controlsRow, styles.detailedControls)}>
            {showRewind && (
              <button
                type="button"
                className={styles.skipButton}
                onClick={handleRewind}
                disabled={!isReady}
                aria-label={`Rewind ${rewindSeconds} seconds`}
              >
                <ArrowCounterClockwise size={skipButtonSize} weight="bold" color={iconColor} />
                <span className={styles.skipLabel} style={{ color: iconColor }}>
                  {rewindSeconds}
                </span>
              </button>
            )}

            {!isPlaybackLocked && (
              <PlayButton
                isReady={isReady}
                isPlaybackLocked={isPlaybackLocked}
                isPlaying={isPlaying}
                isBuffering={isBuffering}
                playButtonSize={playButtonSize}
                iconColor={iconColor}
                togglePlay={togglePlay}
                onPlayAttempt={onPlayAttempt}
              />
            )}

            {showForward && (
              <button
                type="button"
                className={styles.skipButton}
                onClick={handleForward}
                disabled={!isReady}
                aria-label={`Forward ${forwardSeconds} seconds`}
              >
                <ArrowClockwise size={skipButtonSize} weight="bold" color={iconColor} />
                <span className={styles.skipLabel} style={{ color: iconColor }}>
                  {forwardSeconds}
                </span>
              </button>
            )}

            <div className={styles.detailedMetadata}>
              {showTitle && title && (
                <p className={styles.detailedTitle}>
                  {title}
                  {duration > 0 && ` · ${formatAudioPlayerTime(duration).replace(/^0/, '')}`}
                </p>
              )}
              {subtitle && <p className={styles.detailedSubtitle}>{subtitle}</p>}
              {isPreview && <span className={styles.previewBadge}>Preview</span>}
            </div>
            <PlaybackSpeedControl value={playbackRate} onChange={setPlaybackRate} color={iconColor} />
          </div>

          <div className={cn(styles.progressRow, styles.detailedProgress)}>
            <span
              className={cn(styles.timeLabel, styles.detailedCurrentTime)}
              style={{ color: textColor || undefined }}
            >
              {formatAudioPlayerTime(currentTime)}
            </span>

            <div className={cn(styles.waveformWrapper, styles.detailedWaveform)} style={{ height: waveformHeight }}>
              {isDecoding && <div className={styles.waveformLoading} style={{ height: waveformHeight }} />}
              <canvas
                ref={canvasRef}
                style={{ width: '100%', height: waveformHeight, display: 'block', cursor: 'pointer' }}
                onClick={handleCanvasClick}
                onKeyDown={handleCanvasKeyDown}
                role="slider"
                aria-valuenow={currentTime}
                aria-valuemin={0}
                aria-valuemax={duration}
                aria-label="Seek audio"
                tabIndex={0}
              />
            </div>

            <span className={cn(styles.timeLabel, styles.detailedDuration)} style={{ color: textColor || undefined }}>
              {formatAudioPlayerTime(duration)}
            </span>
          </div>
        </div>

        {audioUrl && (
          <audio ref={audioRef} id={audioElementId} preload={srcAttached ? 'metadata' : 'none'}>
            <source src={audioUrl} type={audioType} />
            <track kind="captions" src="" label="No captions available" />
          </audio>
        )}
      </div>
    </div>
  );
};
